<!--
  Editor for "message" actions
-->

<template>
  <orderable-box
    icon="el-icon-chat-line-square"
    v-bind="$attrs"
    v-on="$listeners">
    <template slot="content">
      Text response
      <tooltip-icon placement="top">
        You can give several <em>answers</em>, one will randomly be chosen.<br/>
        Insert a <em>local variable</em> using <code>@variable</code>.<br/>
        Insert a <em>context variable</em> using <code>$variable</code>.<br/>
        You can have default values using <code>(@var1|$var2|default text)</code>.<br/>
        To tune the text depending on the user and the regional variable, write it between brackets.<br/>
        For instance, <code>{mister}</code> will be replaced by <strong>"mister"</strong> or <strong>"misses"</strong> depending of the context.
      </tooltip-icon>
    </template>
    <el-form-item
      label="Language"
      :required="required"
      :prop="prop && prop+'.language'">
      <el-filterable-select
        v-model="language"
        :options="languageOptions"
        :disabled="disabled">
      </el-filterable-select>
    </el-form-item>
    <el-form-small-item
      v-for="(text, index) in texts"
      :key="text.key"
      :prop="prop && prop+'.texts.'+index"
      :required="!text.temporary || required && texts.length <= 1"
      :rules="computedRules"
      field="message">
      <el-button-group>
        <el-button v-if="text.temporary" type="info" style="padding: 12px" icon="el-icon-info" :tabindex="-1"></el-button>
        <!-- button to open the slidedown -->
        <el-tooltip v-else
          effect="light" placement="left" :tabindex="-1"
          :content="'# ' + index">
          <el-button type="info" style="padding: 12px"
            :disabled="text.temporary"
            :icon="text.temporary ? 'el-icon-minus' : opened(`texts.${index}.tones`) ? 'el-icon-caret-top' : 'el-icon-caret-bottom'"
            @click.stop="handleShowHideSubform(`texts.${index}.tones`)"
            :tabindex="-1">
          </el-button>
        </el-tooltip>
        <el-response-autocomplete
          v-model="text.value.text"
          :placeholder="placeholder"
          :local-variables="localVariables"
          :context-variables="contextVariables"
          :disabled="disabled"></el-response-autocomplete>
        <el-button
          :icon="text.temporary ? 'el-icon-circle-plus-outline' : 'el-icon-delete'"
          :disabled="disabled || text.temporary"
          @click="deleteText(text.key)"
          :tabindex="-1"></el-button>
      </el-button-group>
      <!-- slidedown -->
      <el-collapse-transition v-if="!text.temporary">
        <el-form-sub label-width="120px" v-show="opened(`texts.${index}.tones`)">
          <fieldset>
            <legend>Tones</legend>
            <!-- "table" headers -->
            <input-headers-group>
              <input-header>Dimension</input-header>
              <input-header>Level</input-header>
              <input-header width="56px"></input-header>
            </input-headers-group>
            <!-- all the entities and synonyms -->
            <el-form-errors v-for="(tone, i) in text.value.tones" :key="i">
              <el-button-group style="width: 100%">
                <!-- name input -->
                <el-form-small-item
                  :prop="'tones.'+i+'.dimension'"
                  :required="false"
                  field="dimension">
                  <el-select v-model="tone.dimension" placeholder="Select a dimension"
                    :tabindex="opened(`texts.${index}.tones`) ? '0' : '-1'">
                    <el-option v-for="(d, i) in state.settings.tone_dimensions" :key="i" :label="d" :value="d"></el-option>
                  </el-select>
                </el-form-small-item>
                <!-- synonym or entity select -->
                <el-form-small-item
                  :prop="'tones.'+index+'.level'"
                  :required="false"
                  field="level">
                  <!-- <el-input v-model="tone.level" type="number" min="1" max="5" step="1"></el-input> -->
                  <el-select v-model="tone.level" placeholder="Select a level"
                    :tabindex="opened(`texts.${index}.tones`) ? '1' : '-1'">
                    <el-option v-for="(l, i) in levelList" :key="i"  :label="l" :value="l"></el-option>
                  </el-select>
                </el-form-small-item>
                <!-- add or delete button for synonyms -->
                <el-button
                  :icon="tone.temporary ? 'el-icon-circle-plus-outline' : 'el-icon-delete'"
                  :disabled="tone.temporary"
                  :tabindex="-1"
                  @click="deleteTone({ text: text.key , key: tone.key })">
                </el-button>
              </el-button-group>
            </el-form-errors>
          </fieldset>
        </el-form-sub>
      </el-collapse-transition>
    </el-form-small-item>
  </orderable-box>
</template>

<script>
// import { StringVarValidator } from '../../utils/validators'
import OrderableBox from '../orderable/OrderableBox.vue'
import ElFilterableSelect from '../element/ElFilterableSelect.vue'
import ElResponseAutocomplete from '../element/ElResponseAutocomplete.vue'
import State from '../../stores/state.js'

export default {
  components: {
    OrderableBox,
    ElFilterableSelect,
    ElResponseAutocomplete,
  },
  name: 'TextActionEditor',
  props: {
    value: Object,
    placeholder: String,
    languageOptions: Array,
    localVariables: Array,
    contextVariables: Array,
    prop: String,
    required: {
      type: Boolean,
      default: true,
    },
    disabled: Boolean,
    rules: [Object, Array],
  },
  data () {
    return {
      language: 'vi-VN',
      texts: [],
      isShowing: '',
      state: State.state,
      levelList: [
        1, 2, 3, 4, 5,
      ],
    }
  },
  watch: {
    value (value) { this.updateValue(value) },
    texts: {
      handler () { this.valueUpdated() },
      deep: true,
    },
    language () { this.valueUpdated() },
  },
  created () {
    this.updateValue(this.value)
  },
  computed: {
    computedRules () {
      // return [ PreOutputValidator, OutputValidator ].concat(this.rules || [])
      return [].concat(this.rules || [])
    },
  },
  methods: {
    // update texts from value
    updateValue (value) {
      const customOriginTexts = value.texts.map(e => {
        if (typeof (e) === 'object') {
          return {
            text: e.text || '',
            tones: e.tones || [],
          }
        } else {
          return {
            text: e,
            tones: [],
          }
        }
      })

      value.texts = [...customOriginTexts]
      this.language = value.language
      let texts = this.texts.filter(t => !t.temporary)
      let temporary = this.texts.find(t => t.temporary)
      let same = texts.length === value.texts.length
      let keys = {}

      if (same) {
        // local texts
        texts.forEach((t, i) => {
          keys[t.value.text] = t
          if (t.value.text !== value.texts[i].text) {
            same = false
          }
        })
      }
      if (!temporary) {
        temporary = {
          key: Math.random() + '',
          temporary: true,
          value: {
            text: '',
            tones: [],
          },
        }
        if (same) this.texts.push(temporary)
      } else {
        texts.forEach((t, i) => {
          // Consider to tones
          let tones = t.value.tones.filter(tone => !tone.temporary)
          if (tones.length !== value.texts[i].tones.length) {
            same = false
            tones.forEach((tone, j) => {
              if (tone.dimension !== value.texts[i].tones[j].dimension || tone.level !== value.texts[i].tones[j].level) {
                same = false
              }
            })
          }
        })
      }
      if (!same) {
        texts = value.texts.map((val) => {
          return keys[val.text] || {
            key: Math.random() + '',
            value: val,
          }
        })

        texts.forEach((t) => {
          let temporaryTone = t.value.tones.find(tone => tone.temporary)
          if (!temporaryTone) {
            t.value.tones.push({ dimension: null, level: null, temporary: true, key: 'tone' + Math.random() + '' })
          }
        })
        texts.push(temporary)
        this.texts = texts
      }
    },
    // emit value from texts
    valueUpdated () {
      let temporary = this.texts.find(t => t.temporary)
      if (temporary && temporary.value.text) {
        delete temporary.temporary
        temporary = null
      }
      if (!temporary) {
        temporary = {
          key: Math.random() + '',
          temporary: true,
          value: {
            text: '',
            tones: [],
          },
        }
        this.texts.push(temporary)
      }

      // consider to Tones
      let texts = this.texts.filter(t => !t.temporary).map(t => {
        t.value.tones.forEach(e => {
          if (e.dimension) {
            e.level = Number(e.level) < 1 ? 3 : e.level
          }
        })
        let temporaryTone = t.value.tones.find(tone => tone.temporary)
        if (temporaryTone && (temporaryTone.dimension || temporaryTone.level)) {
          delete temporaryTone.temporary
          temporaryTone = null
        }
        if (!temporaryTone) {
          temporaryTone = { dimension: null, level: null, temporary: true, key: 'tone' + Math.random() + '' }
          t.value.tones.push(temporaryTone)
        }

        let e = { ...t.value }
        e.tones = e.tones.filter(tone => !tone.temporary).map(m => {
          delete m.key
          return m
        })
        return e
      })
      this.$emit('input', {
        ...this.value,
        language: this.language,
        texts: texts,
      })
    },
    // delete a text
    deleteText (key) {
      for (let i = 0; i < this.texts.length; i++) {
        if (this.texts[i].key === key &&
            !this.texts[i].temporary) {
          this.texts.splice(i, 1)
          return
        }
      }
      throw new TypeError('key "' + key + '" not found')
    },
    // delete a tone
    deleteTone (value) {
      const textObject = this.texts.find(text => text.key === value.text)
      if (textObject) {
        let index = textObject.value.tones.findIndex(tone => tone.key === value.key)
        if (index > -1) {
          textObject.value.tones.splice(index, 1)
          return
        }
      }
      throw new TypeError('key "' + value.key + '" not found')
    },
    handleShowHideSubform (key) {
      this.isShowing = this.isShowing === key ? '' : key
    },
    opened (key) {
      return this.isShowing === key
    },
  },
}
</script>
